import { useCallback, useState } from 'react';

export type UseListNavigationOptions = {
  itemCount: number;
  /** When true, wraps from last to first and vice versa. */
  loop?: boolean;
  /** Called when the active index changes. */
  onActiveChange?: (index: number) => void;
};

/**
 * Keyboard arrow / Home / End navigation for menus and listboxes.
 *
 * Returns:
 * - `activeIndex`: currently highlighted item index (-1 = none).
 * - `setActiveIndex`: setter for external control.
 * - `handleKeyDown`: attach to the list container's onKeyDown.
 */
export function useListNavigation({
  itemCount,
  loop = true,
  onActiveChange,
}: UseListNavigationOptions) {
  const [activeIndex, setActiveIndexState] = useState(-1);

  const setActiveIndex = useCallback(
    (index: number) => {
      setActiveIndexState(index);
      onActiveChange?.(index);
    },
    [onActiveChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (itemCount === 0) return;

      let next = activeIndex;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (activeIndex < 0) {
            next = 0;
          } else if (activeIndex >= itemCount - 1) {
            next = loop ? 0 : itemCount - 1;
          } else {
            next = activeIndex + 1;
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (activeIndex <= 0) {
            next = loop ? itemCount - 1 : 0;
          } else {
            next = activeIndex - 1;
          }
          break;
        case 'Home':
          e.preventDefault();
          next = 0;
          break;
        case 'End':
          e.preventDefault();
          next = itemCount - 1;
          break;
        default:
          return;
      }

      if (next !== activeIndex) {
        setActiveIndex(next);
      }
    },
    [activeIndex, itemCount, loop, setActiveIndex],
  );

  return { activeIndex, setActiveIndex, handleKeyDown };
}
